import React from "react";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { AssetIcon } from "@/components/common/AssetIcon";
import { DropdownMenu } from "components/common/DropdownMenu";
import { DropdownMenuItem } from "components/common/DropdownMenuItem";
import type { ModelElementType } from "components/entities";

/** The field-like button that opens a space, app or model menu: what is
 *  picked, else what to pick, muted. */
export function PickerTrigger({
  ref,
  open,
  onToggle,
  disabled,
  lead,
  label,
  placeholder,
  trail,
}: {
  ref: React.Ref<HTMLButtonElement>;
  open: boolean;
  onToggle: () => void;
  disabled?: boolean;
  lead?: React.ReactNode;
  /** What is picked; absent, the placeholder shows. */
  label?: string;
  placeholder: string;
  /** In place of the chevron, e.g. a lock on a pick that cannot change. */
  trail?: React.ReactNode;
}) {
  return (
    // eslint-disable-next-line no-restricted-syntax -- a picker field, not an action button
    <button
      ref={ref}
      type="button"
      aria-haspopup="listbox"
      aria-expanded={open}
      disabled={disabled}
      onClick={onToggle}
      className="flex w-full items-center gap-2 rounded-app border border-border px-2 enabled:hover:bg-bg-hover text-left cursor-pointer disabled:cursor-not-allowed disabled:opacity-55"
      style={{ minHeight: "var(--control-height-md)" }}
    >
      {lead}
      <span
        className={`flex-1 truncate ${label ? "text-fg" : "text-fg-muted"}`}
      >
        {label ?? placeholder}
      </span>
      {trail ?? (
        <ExpandMoreIcon sx={{ fontSize: 16 }} className="text-fg-muted" />
      )}
    </button>
  );
}

export function ModelMark({ model }: { model: ModelElementType }) {
  return (
    <AssetIcon
      size="sm"
      color={model.color}
      iconIndex={model.icon}
      className="w-3! h-3!"
    />
  );
}

/** A list's height at five rows: a scrolled list ends on a whole row instead of showing the top of the next.
 *  Set on a box with no vertical padding, which the next row would show through. */
export const FIVE_ROWS = "calc(5 * var(--item-height))";

/** A space's models to pick one from. */
export function ModelListMenu({
  anchorRef,
  isOpen,
  onClose,
  models,
  selected,
  onPick,
}: {
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  isOpen: boolean;
  onClose: () => void;
  models: ModelElementType[];
  selected: string | null;
  onPick: (modelId: string) => void;
}) {
  return (
    <DropdownMenu
      anchorRef={anchorRef}
      isOpen={isOpen}
      onClose={onClose}
      className="py-1"
    >
      <div
        className="overflow-y-auto scrollbar-overlay-hover"
        style={{ maxHeight: FIVE_ROWS }}
      >
        {models.map((m) => (
          <DropdownMenuItem
            key={m.model_id}
            isSelected={m.model_id === selected}
            onClick={() => onPick(m.model_id)}
          >
            <span className="flex w-full min-w-0 items-center gap-2">
              <ModelMark model={m} />
              <span className="min-w-0 flex-1 truncate text-left">
                {m.name}
              </span>
            </span>
          </DropdownMenuItem>
        ))}
      </div>
    </DropdownMenu>
  );
}
